import { Injectable, OnDestroy } from '@angular/core';

/**
 * App-wide: when a searchable ng-select opens, focus its filter input after
 * the opened class / styles paint so the text caret shows immediately
 * (ng-select focuses before change detection, which can leave the caret hidden).
 */
@Injectable({
  providedIn: 'root',
})
export class NgSelectSearchFocusService implements OnDestroy {
  private listening = false;
  private pendingFocus: number | null = null;

  startWatching(): void {
    if (this.listening || typeof document === 'undefined') {
      return;
    }

    // Capture after ng-select's own handlers open the panel (mousedown / keyboard).
    document.addEventListener('mousedown', this.onMouseDown, true);
    document.addEventListener('keydown', this.onKeyDown, true);
    this.listening = true;
  }

  stopWatching(): void {
    if (this.pendingFocus !== null) {
      cancelAnimationFrame(this.pendingFocus);
      this.pendingFocus = null;
    }

    if (!this.listening || typeof document === 'undefined') {
      return;
    }

    document.removeEventListener('mousedown', this.onMouseDown, true);
    document.removeEventListener('keydown', this.onKeyDown, true);
    this.listening = false;
  }

  ngOnDestroy(): void {
    this.stopWatching();
  }

  private readonly onMouseDown = (event: MouseEvent): void => {
    const select = this.findSearchableSelect(event.target);
    if (!select) {
      return;
    }
    // Defer until after ng-select open() + CD apply .ng-select-opened styles.
    this.scheduleFocus(select);
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const openKeys = ['Enter', ' ', 'ArrowDown', 'Down'];
    if (!openKeys.includes(event.key)) {
      return;
    }

    const select = this.findSearchableSelect(event.target);
    if (!select) {
      return;
    }

    this.scheduleFocus(select);
  };

  private findSearchableSelect(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) {
      return null;
    }

    const select = target.closest('.ng-select');
    if (
      !(select instanceof HTMLElement) ||
      !select.classList.contains('ng-select-searchable')
    ) {
      return null;
    }

    return select;
  }

  private scheduleFocus(selectEl: HTMLElement): void {
    if (this.pendingFocus !== null) {
      cancelAnimationFrame(this.pendingFocus);
    }

    // Double rAF: wait until ng-select-opened styles (opacity/pointer-events) apply.
    this.pendingFocus = requestAnimationFrame(() => {
      this.pendingFocus = requestAnimationFrame(() => {
        this.pendingFocus = null;
        this.focusSearchInput(selectEl);
      });
    });
  }

  private focusSearchInput(selectEl: HTMLElement): void {
    if (
      !selectEl.isConnected ||
      !selectEl.classList.contains('ng-select-opened') ||
      !selectEl.classList.contains('ng-select-searchable')
    ) {
      return;
    }

    const input = selectEl.querySelector(
      '.ng-input > input:not([readonly])'
    ) as HTMLInputElement | null;

    if (!input || input.disabled) {
      return;
    }

    if (document.activeElement !== input) {
      input.focus({ preventScroll: true });
    }

    // Nudge caret paint after opacity becomes visible (no blur — that can close the panel).
    try {
      const len = input.value?.length ?? 0;
      input.setSelectionRange(len, len);
    } catch {
      // Ignore inputs that do not support selection ranges.
    }
  }
}
